/**
 * XIV Dye Tools v4.0 - Tool Configuration Types
 *
 * TypeScript interfaces for all tool configurations.
 * Used by ConfigController and ConfigSidebar for type-safe config management.
 *
 * @module shared/tool-config-types
 */

import type { ToolId } from '@services/router-service';
import type { ColorWheelId, MatchingMethod } from '@xivdyetools/core';
import type { DyeTypeFilters, PresetSortOption } from '@xivdyetools/types';
import { COMPANION_DYES_DEFAULT } from '@shared/constants';

// Re-export MatchingMethod for convenience
export type { MatchingMethod } from '@xivdyetools/core';

/** Pixel sampling area size for Shift+Click (NxN pixels) */
export type SampleAreaSize = 1 | 2 | 4 | 8 | 16;

// ============================================================================
// Global Configuration
// ============================================================================

/**
 * Global application configuration (not tool-specific)
 */
export interface GlobalConfig {
  /** Selected theme name */
  theme: string;
  /** Global display options shared across all tools */
  displayOptions: DisplayOptionsConfig;
  /** Global dye filter configuration shared across all tools */
  dyeFilters: DyeFiltersConfig;
}

// ============================================================================
// Tool-Specific Configurations
// ============================================================================

/**
 * Harmony Explorer configuration
 */
export interface HarmonyConfig {
  /** Selected harmony type (complementary, analogous, triadic, etc.) */
  harmonyType: string;
  /** Colour wheel the harmony angles are measured on (core `ColorWheelId`); default `rgb` */
  wheel: ColorWheelId;
  /** Use perceptual (DeltaE) color matching instead of hue-based */
  strictMatching: boolean;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Prevent the same dye from appearing in multiple harmony slots */
  preventDuplicates: boolean;
  /** Companion alternates offered per slot as swatch dots (1-5) */
  companionDyesCount: number;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Palette Extractor (Color Matcher) configuration
 */
export interface ExtractorConfig {
  /** Enable vibrancy boost for extracted colors */
  vibrancyBoost: boolean;
  /** Maximum number of colors to extract (3-10) */
  maxColors: number;
  /** Drag threshold in pixels for click vs drag differentiation (3-15, default 5) */
  dragThreshold: number;
  /** Pixel sample area size for Shift+Click sampling (NxN, default 1) */
  sampleAreaSize: SampleAreaSize;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Prevent the same dye from appearing in multiple palette slots */
  preventDuplicates: boolean;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Accessibility Checker configuration
 */
export interface AccessibilityConfig {
  /** Show normal vision preview */
  normalVision: boolean;
  /** Show deuteranopia (red-green) simulation */
  deuteranopia: boolean;
  /** Show protanopia (red-blind) simulation */
  protanopia: boolean;
  /** Show tritanopia (blue-yellow) simulation */
  tritanopia: boolean;
  /** Show achromatopsia (complete colorblindness) simulation */
  achromatopsia: boolean;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
}

/**
 * Dye Comparison configuration
 * Uses shared displayOptions for all color format and metadata visibility settings.
 */
export interface ComparisonConfig {
  /** The ΔE2000 SAME cut ("match line", 1-15) the duel verdict follows */
  matchThreshold: number;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
}

/**
 * Color interpolation mode for the Gradient Builder tool
 * - 'rgb': Linear RGB interpolation (gray midpoints for complements)
 * - 'hsv': HSV hue interpolation with wraparound
 * - 'lab': CIE LAB perceptual interpolation
 * - 'oklch': OKLCH perceptual interpolation (best for gradients)
 * - 'lch': LCH (cylindrical LAB) interpolation
 */
export type InterpolationMode = 'rgb' | 'hsv' | 'lab' | 'oklch' | 'lch';

/**
 * Gradient Builder (Dye Mixer) configuration
 */
export interface GradientConfig {
  /** Number of gradient steps (3-12) */
  stepCount: number;
  /** Interpolation color space mode */
  interpolation: InterpolationMode;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Stop consecutive ramp steps resolving to the same dye */
  preventDuplicates: boolean;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Color mixing algorithm for the Dye Mixer tool
 * - 'rgb': RGB additive mixing (light-based, Blue + Yellow = Gray)
 * - 'lab': LAB perceptually uniform blending (Blue + Yellow = Pink)
 * - 'oklab': OKLAB perceptually uniform blending (Blue + Yellow = Cyan)
 * - 'ryb': RYB subtractive mixing on the artist's wheel (Blue + Yellow = Green)
 * - 'hsl': HSL hue-based blending
 * - 'spectral': Kubelka-Munk spectral mixing (realistic paint, Blue + Yellow = Green)
 */
export type MixingMode = 'rgb' | 'lab' | 'oklab' | 'ryb' | 'hsl' | 'spectral';

/**
 * Dye Mixer (NEW in v4) configuration
 */
export interface MixerConfig {
  /** Maximum results to show (3-8) */
  maxResults: number;
  /** Color mixing algorithm (default: 'ryb' for paint-like mixing) */
  mixingMode: MixingMode;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Preset category types (matches backend categories)
 */
export type PresetCategoryFilter =
  | 'all'
  | 'jobs'
  | 'grand-companies'
  | 'seasons'
  | 'events'
  | 'aesthetics'
  | 'appearance'
  | 'zones'
  | 'raids-trials';

// PresetSortOption is the shared `@xivdyetools/types` contract, re-exported
// here so existing `@shared/tool-config-types` imports keep working.
export type { PresetSortOption };

/**
 * Community Presets configuration
 */
export interface PresetsConfig {
  /** Sort order (page control; mirrored here for persistence) */
  sortBy: PresetSortOption;
  /** Category filter (page rail; mirrored here for persistence) */
  category: PresetCategoryFilter;
  /** Feed: show example-link areas on cards (off = every card a palette strip) */
  feedShots: boolean;
  /** Feed: mix curated palettes into the Community tab (off = own tab only) */
  feedBlend: boolean;
  /** Feed: hide palettes containing market-only (coffer) dyes */
  feedHideUnbuyable: boolean;
  /** Saved shelf: pin saved presets to the top of every tab */
  savedFirst: boolean;
  /** Saved shelf: keep author-deleted saved presets, marked */
  keepDeleted: boolean;
  /** Display options for dye cards in preset details */
  displayOptions: DisplayOptionsConfig;
}

/**
 * Budget Suggestions configuration
 */
export interface BudgetConfig {
  /** Match line: how far a substitute may drift (ΔE2000, 2-20) */
  maxDeltaE: number;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Swatch Matcher configuration (locale namespace `tools.character`, the v3 name)
 */
export interface SwatchConfig {
  /**
   * Selected color sheet category.
   * Values: 'eyeColors', 'hairColors', 'skinColors', 'highlightColors',
   * 'lipColorsDark', 'lipColorsLight', 'tattooColors',
   * 'facePaintColorsDark', 'facePaintColorsLight'
   */
  colorSheet: string;
  /**
   * Selected subrace (SubRace type).
   * Values: 'Midlander', 'Highlander', 'Wildwood', 'Duskwight',
   * 'Plainsfolk', 'Dunesfolk', 'SeekerOfTheSun', 'KeeperOfTheMoon',
   * 'SeaWolf', 'Hellsguard', 'Raen', 'Xaela', 'Helions', 'TheLost',
   * 'Rava', 'Veena'
   */
  race: string;
  /** Selected gender: 'Male' or 'Female' */
  gender: string;
  /** Maximum results to show (1-6) */
  maxResults: number;
  /** Color matching algorithm for finding closest dyes */
  matchingMethod: MatchingMethod;
  /** Display options for result cards */
  displayOptions: DisplayOptionsConfig;
  /** Dye filter configuration */
  dyeFilters: DyeFiltersConfig;
}

/**
 * Market Board configuration (shared across tools)
 */
export interface MarketConfig {
  /** Selected server (data center name or world name) */
  selectedServer: string;
  /** Whether to show market prices */
  showPrices: boolean;
}

/**
 * Advanced Settings configuration (debug/utility options)
 */
export interface AdvancedConfig {
  /** Opt-in usage telemetry — read by TelemetryService (docs/superpowers/specs/2026-08-29-web-analytics-design.md) */
  analyticsEnabled: boolean;
  /** Enable performance mode (reduce animations, disable blur effects) */
  performanceMode: boolean;
}

// ============================================================================
// Shared Dye Filters
// ============================================================================

/**
 * Shared dye filter configuration for excluding dye categories from results.
 * Controls which dye types and acquisition sources are filtered out.
 *
 * All fields from DyeTypeFilters are required since the web-app UI
 * always tracks the full set of filter toggles. `excludeCoffers` is the
 * web-side overlay for the twenty market-only Venture Coffer dyes —
 * core's excludeCraftDyes bundles them with Firmament and can't express
 * coffer-only (see shared/dye-filter-utils).
 */
export type DyeFiltersConfig = Required<DyeTypeFilters> & { excludeCoffers: boolean };

/**
 * Default dye filter configuration (all filters disabled)
 */
export const DEFAULT_DYE_FILTERS: DyeFiltersConfig = {
  excludeMetallic: false,
  excludePastel: false,
  excludeDark: false,
  excludeCosmic: false,
  excludeIshgardian: false,
  excludeExpensive: false,
  excludeVendorDyes: false,
  excludeCraftDyes: false,
  excludeCoffers: false,
};

// ============================================================================
// Shared Display Options
// ============================================================================

/**
 * Shared display options for result cards across tools.
 * Controls visibility of color formats and result metadata.
 */
export interface DisplayOptionsConfig {
  // Color format visibility
  /** Show HEX color codes */
  showHex: boolean;
  /** Show RGB values */
  showRgb: boolean;
  /** Show HSV values */
  showHsv: boolean;
  /** Show LAB values */
  showLab: boolean;
  /** Show CMYK values */
  showCmyk: boolean;

  // Result metadata visibility
  /** Show market prices */
  showPrice: boolean;
  /**
   * @deprecated The ΔE2000 verdict is structural on the 5.0 Result Card and
   * always renders; this flag no longer controls anything on the card.
   */
  showDeltaE: boolean;
  /** Show acquisition source information */
  showAcquisition: boolean;

  // 5.0 Ticket optional fields (persistent, default all-on).
  // Optional so configs persisted before 5.0 stay valid — read with `?? true`.
  /** Show the HUE OFF readout beside the verdict */
  showHue?: boolean;
  /** Show the STAIN readout beside the verdict */
  showStain?: boolean;
  /** Show the consolidated dye spectrum row */
  showSpectrum?: boolean;
}

/**
 * Default display options for result cards
 */
export const DEFAULT_DISPLAY_OPTIONS: DisplayOptionsConfig = {
  showHex: true,
  showRgb: true,
  showHsv: true,
  showLab: true,
  showCmyk: false,
  showPrice: true,
  showDeltaE: true,
  showAcquisition: true,
  showHue: true,
  showStain: true,
  showSpectrum: true,
};

// ============================================================================
// Type Mapping
// ============================================================================

/**
 * Map of tool IDs to their configuration interfaces
 */
export interface ToolConfigMap {
  global: GlobalConfig;
  market: MarketConfig;
  advanced: AdvancedConfig;
  harmony: HarmonyConfig;
  extractor: ExtractorConfig;
  accessibility: AccessibilityConfig;
  comparison: ComparisonConfig;
  gradient: GradientConfig;
  mixer: MixerConfig;
  presets: PresetsConfig;
  budget: BudgetConfig;
  swatch: SwatchConfig;
}

/**
 * Union type of all possible tool config types
 */
export type ToolConfig = ToolConfigMap[keyof ToolConfigMap];

/**
 * Config key type: every tool with settings, plus the shared sections
 * ('global', 'market', 'advanced'). The Glamour Reader has
 * none (spec G11: tribe and gender come from the file, nothing is a colour
 * match), so it has no config slot.
 */
export type ConfigKey = Exclude<ToolId, 'glamour'> | 'global' | 'market' | 'advanced';

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default configuration values for all tools
 */
const DEFAULT_CONFIGS: ToolConfigMap = {
  global: {
    theme: '',
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  market: {
    selectedServer: 'Crystal',
    showPrices: false,
  },
  advanced: {
    analyticsEnabled: false,
    performanceMode: false,
  },
  harmony: {
    harmonyType: 'complementary',
    wheel: 'rgb',
    strictMatching: true,
    matchingMethod: 'ciede2000',
    preventDuplicates: true,
    companionDyesCount: COMPANION_DYES_DEFAULT,
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  extractor: {
    vibrancyBoost: true,
    maxColors: 4,
    dragThreshold: 5,
    sampleAreaSize: 1,
    matchingMethod: 'ciede2000',
    preventDuplicates: true,
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  accessibility: {
    normalVision: true,
    deuteranopia: true,
    protanopia: true,
    tritanopia: true,
    achromatopsia: true,
    displayOptions: {
      ...DEFAULT_DISPLAY_OPTIONS,
      showPrice: false, // Accessibility tool doesn't show prices by default
      showDeltaE: false, // Not applicable for accessibility
      showAcquisition: false, // Keep focus on colors
    },
  },
  comparison: {
    matchThreshold: 5,
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
  },
  gradient: {
    stepCount: 8,
    interpolation: 'hsv',
    matchingMethod: 'ciede2000',
    preventDuplicates: true,
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  mixer: {
    maxResults: 4,
    mixingMode: 'ryb',
    matchingMethod: 'ciede2000',
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  presets: {
    sortBy: 'popular',
    category: 'all',
    feedShots: true,
    feedBlend: false,
    feedHideUnbuyable: false,
    savedFirst: true,
    keepDeleted: true,
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
  },
  budget: {
    maxDeltaE: 8,
    matchingMethod: 'ciede2000',
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
  // BUG-001 (2026-10-04 deep-dive): these were hairColors / SeekerOfTheSun /
  // Female while the Swatch tool opened on Eye / Midlander / Male, and the
  // controller's full-config broadcast flipped the tool to these. One table now.
  swatch: {
    colorSheet: 'eyeColors',
    race: 'Midlander',
    gender: 'Male',
    maxResults: 3,
    matchingMethod: 'ciede2000',
    displayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
    dyeFilters: { ...DEFAULT_DYE_FILTERS },
  },
};

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Get the default config for a tool
 */
export function getDefaultConfig<K extends ConfigKey>(key: K): ToolConfigMap[K] {
  return DEFAULT_CONFIGS[key];
}
